/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';

import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useAutocomplete } from '@material-ui/lab';
import Checkbox from '@material-ui/core/Checkbox';

const useStyles = makeStyles(theme => ({
  tag: {
    display: 'flex',
    alignItems: 'center',
    height: '1.5rem',
    margin: '0.2rem',
    lineHeight: '1.3rem',
    background: theme.palette.validTagBackground,
    borderRadius: '12px',
    boxSizing: 'content-box',
    padding: '0 0.2rem 0 0.5rem',
    outline: 0,
    overflow: 'hidden',
    color: 'white',

    '& span': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '1rem',
    },

    '& svg': {
      fontSize: '0.9rem',
      cursor: 'pointer',
      padding: '0.2rem',
    },
  },
  invalidTag: {
    background: theme.palette.error.main,
  },
  root: {
    fontSize: '1rem',
    color: 'rgba(0,0,0,.85)',
  },
  inputWrapper: {
    width: '100%',
    position: 'relative',
    padding: '0.5rem 1rem',
    borderRadius: '0.2rem',
    border: `1px solid ${theme.palette.lightBorder}`,
    outline: 'none',
    backgroundColor: theme.palette.white,

    fontSize: '1rem',
    color: theme.palette.mainText,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',

    '&.focused': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 5px ${theme.palette.focus}`,
    },

    '& input': {
      height: '2rem',
      boxSizing: 'border-box',
      padding: '0.2rem 0.3rem',
      width: 0,
      minWidth: '2rem',
      flexGrow: 1,
      border: 0,
      margin: 0,
      outline: 0,
    },
  },

  listBox: {
    width: '100%',
    maxWidth: '31rem',
    margin: '0.125rem 0 0',
    padding: '0.23rem',
    position: 'absolute',
    listStyle: 'none',
    backgroundColor: '#fff',
    overflow: 'auto',
    borderRadius: '0.2rem',
    border: `1px solid ${theme.palette.border}`,
    zIndex: 1,

    '& li': {
      display: 'flex',
      padding: '0.125rem',
      alignItems: 'center',
      height: '3.125rem',

      '&:hover': {
        backgroundColor: theme.palette.keyboardFocusBackground,
        borderRadius: '0.2rem',
      },

      '& span': {
        fonstSize: '1rem',
      },

      '& svg': {},
    },
  },
  icon: {
    borderRadius: '0.2rem',
    '-webkit-border-radius': '0.2rem',
    '-khtml-border-radius': '0.2rem',
    '-moz-border-radius': '0.2rem',
    width: '1rem',
    height: '1rem',
    backgroundColor: theme.palette.secondary.active,
    position: 'relative',
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%,-50%)',
      width: '1.25rem',
      height: '1.25rem',
      border: `1px solid ${theme.palette.input}`,
      borderRadius: '0.3rem',
    },
    '.Mui-focusVisible &': {
      backgroundColor: theme.palette.darkGray,
      '&:after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%,-50%)',
        width: '1.5rem',
        height: '1.5rem',
        border: `3px solid ${theme.palette.primary.main}`,
        borderRadius: '0.3rem',
      },
    },
    'input:hover ~ &': {
      backgroundColor: theme.palette.darkGray,
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: theme.palette.lightGray,
    },
  },
  checkedIcon: {
    position: 'relative',
    width: '1rem',
    height: '1rem',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '0.2rem',
    '&::after': {
      content: '""',
      top: '45%',
      left: '50%',
      width: '.75rem',
      height: '.35rem',
      position: 'absolute',
      transform: 'translate(-50%, -50%) rotate(-45deg)',
      borderLeft: '0.15rem solid white',
      borderBottom: '0.15rem solid white',
    },
  },
}));

function Tag({ isValid, label, onDelete, classes, ...tagProps }) {
  const className = clsx({ [classes.tag]: true, [classes.invalidTag]: !isValid });

  return (
    <div {...tagProps} className={className}>
      <span>{label}</span>
      <CloseIcon onClick={onDelete} />
    </div>
  );
}

export default function SelectWithSearch({
  name,
  options,
  onKeyUp,
  defaultValue,
  onChange,
  customValidation,
  enableAutocomplete = true,
  placeholder,
}) {
  const {
    getRootProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    id: 'selectWithSearch',
    defaultValue,
    multiple: true,
    options: enableAutocomplete ? options ?? [] : [],
    freeSolo: true,
    getOptionLabel: option => option?.label,
  });

  const hasValue = value.length > 0;
  const classes = useStyles();
  useEffect(() => onChange(value), [value]);

  const renderTag = (option, index) => {
    let isValid = true;
    if (customValidation && typeof customValidation === 'function') {
      isValid = customValidation(option);
    }

    return <Tag isValid={isValid} label={option?.label ?? option} {...getTagProps({ index })} classes={classes} />;
  };

  return (
    <div className={classes.root}>
      <div {...getRootProps()}>
        <div
          className={`${classes.inputWrapper} ${focused ? 'focused' : ''} ${value ? 'valid' : ''}`}
          ref={setAnchorEl}
        >
          {value?.map((option, index) => renderTag(option, index))}

          <input
            name={name}
            data-testid="select-with-search-input"
            className={classes.input}
            placeholder={!hasValue && placeholder}
            onKeyUp={(evt) => {
              if (enableAutocomplete) {
                onKeyUp(evt.target.value);
              }
            }}
            {...getInputProps()}
          />

          {enableAutocomplete && <SearchIcon />}
        </div>
      </div>

      {groupedOptions.length > 0 ? (
        <ul className={classes.listBox} {...getListboxProps()}>
          {groupedOptions.map((option, index) => (
            <li {...getOptionProps({ option, index })}>
              <Checkbox
                data-testid={`text-checkbox-${option.value}`}
                value={option.value}
                checked={!!value?.find(item => item.value === option.value)}
                color="primary"
                checkedIcon={<span className={classes.checkedIcon} />}
                icon={<span className={classes.icon} />}
              />
              <span>{option?.label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
